import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
	type ReactNode,
} from "react";
import { requestApi } from "../core/api/api";
import { getSession } from "../core/session/session";
import { useAuthSessionContext } from "../components/routing/auth-session-store";
import { StatusOrder } from "../core/types/services";
import type { OrderResponse, ReviewResponse } from "../core/types/services";
import { ModalPendingReview } from "../components/modals/ModalPendingReview";

// ── Types ─────────────────────────────────────────────────────────────────────

export type PendingOrderForReview = {
	orderId: number;
	orderCode: string;
	providerName: string;
	/** user_id del proveedor — qualified_id en la reseña */
	providerId: number;
	/** user_id del cliente — qualifier_id en la reseña */
	qualifierId: number;
};

type PendingReviewContextValue = {
	pendingOrder: PendingOrderForReview | null;
	dismiss: () => void;
	markReviewed: (orderId: number) => void;
};

// ── Context ───────────────────────────────────────────────────────────────────

const PendingReviewContext = createContext<PendingReviewContextValue | undefined>(
	undefined,
);

// eslint-disable-next-line react-refresh/only-export-components
export function usePendingReview(): PendingReviewContextValue {
	const ctx = useContext(PendingReviewContext);
	if (!ctx) {
		throw new Error("usePendingReview must be used within PendingReviewProvider");
	}
	return ctx;
}

// ── Eventos de auth ───────────────────────────────────────────────────────────

/** Disparado desde auth.ts tras login exitoso */
export const POST_LOGIN_EVENT = "servix_post_login";
/** Disparado desde auth.ts al cerrar sesión */
const LOGOUT_EVENT = "servix_logout";

// ── localStorage helpers (clave aislada por usuario) ─────────────────────────

function dismissedKey(userId: number): string {
	return `servix_review_dismissed_${userId}`;
}

function readDismissed(userId: number): Set<number> {
	try {
		const raw = localStorage.getItem(dismissedKey(userId));
		return raw ? new Set(JSON.parse(raw) as number[]) : new Set();
	} catch {
		return new Set();
	}
}

function writeDismissed(userId: number, ids: Set<number>): void {
	localStorage.setItem(dismissedKey(userId), JSON.stringify([...ids]));
}

function clearDismissed(userId: number): void {
	localStorage.removeItem(dismissedKey(userId));
}

// ── Helpers de datos ─────────────────────────────────────────────────────────

/** Extrae el client.id desde la respuesta normalizada de /users/:id */
function extractClientId(payload: unknown): number | null {
	const parseId = (v: unknown): number | null => {
		if (typeof v === "number" && v > 0) return v;
		if (typeof v === "string") {
			const n = Number(v);
			return Number.isFinite(n) && n > 0 ? n : null;
		}
		return null;
	};
	if (!payload || typeof payload !== "object") return null;
	const root = payload as Record<string, unknown>;

	// api.ts normaliza /users/:id → { user: { id, ..., clients: { id: X } } }
	const candidates = [
		root.clients,
		(root.user as Record<string, unknown> | undefined)?.clients,
	];
	for (const entry of candidates) {
		if (Array.isArray(entry)) {
			for (const item of entry as { id?: unknown }[]) {
				const id = parseId(item?.id);
				if (id) return id;
			}
		} else if (entry && typeof entry === "object") {
			const id = parseId((entry as { id?: unknown }).id);
			if (id) return id;
		}
	}
	return null;
}

function buildProviderName(order: OrderResponse): string {
	const company = order.providers?.company_name?.trim() ?? "";
	if (company && company.toLowerCase() !== "proveedor independiente") return company;
	return (
		[order.providers?.users?.names, order.providers?.users?.last_names]
			.filter(Boolean)
			.join(" ") || "Proveedor"
	);
}

/**
 * Realiza las 3 llamadas para obtener órdenes completadas sin reseña.
 *
 * Normalización de api.ts:
 *  - /users/:id           → { user: { ..., clients: { id } } }
 *  - /orders/client/:id   → { orders: [...], meta }   (NO 'items')
 *  - /reviews/qualifier/:id → { reviews: [...] }       (NO array directo)
 */
async function fetchPendingOrders(userId: number): Promise<PendingOrderForReview[]> {
	const userPayload = await requestApi<unknown>("GET", `/users/${userId}`);
	const clientId = extractClientId(userPayload);
	if (!clientId) return [];

	const ordersRaw = await requestApi<Record<string, unknown>>(
		"GET",
		`/orders/client/${clientId}?page=1&pageSize=100`,
	);
	const allOrders = ((ordersRaw as { orders?: OrderResponse[] }).orders ??
		(ordersRaw as { items?: OrderResponse[] }).items ??
		[]) as OrderResponse[];

	const completedOrders = allOrders.filter(
		(o) =>
			o.status_order === StatusOrder.COMPLETED ||
			o.status_order === StatusOrder.PAID,
	);
	if (completedOrders.length === 0) return [];

	const reviewsRaw = await requestApi<unknown>(
		"GET",
		`/reviews/qualifier/${userId}`,
	).catch(() => null);

	const reviewsList: ReviewResponse[] = Array.isArray(reviewsRaw)
		? (reviewsRaw as ReviewResponse[])
		: ((reviewsRaw as { reviews?: ReviewResponse[] } | null)?.reviews ?? []);

	const reviewedOrderIds = new Set(reviewsList.map((r) => r.order_id));

	return completedOrders
		.filter((o) => !reviewedOrderIds.has(o.id))
		.map((o) => ({
			orderId: o.id,
			orderCode: o.code_order,
			providerName: buildProviderName(o),
			providerId: o.providers?.users?.id ?? 0,
			qualifierId: userId,
		}))
		.filter((o) => o.providerId > 0);
}

// ── Provider ──────────────────────────────────────────────────────────────────

export function PendingReviewProvider({ children }: { children: ReactNode }) {
	const { session, isAuthenticated, role } = useAuthSessionContext();
	const [pendingOrders, setPendingOrders] = useState<PendingOrderForReview[]>([]);
	const [dismissed, setDismissed] = useState<Set<number>>(() => new Set());

	/** userId activo en memoria (para poder limpiar localStorage al cerrar sesión) */
	const activeUserIdRef = useRef<number | null>(null);
	const loadingRef = useRef(false);
	const loadedForUserRef = useRef<number | null>(null);

	// ── Helpers de limpieza de sesión ────────────────────────────────────────
	const resetSession = useCallback(() => {
		if (activeUserIdRef.current) {
			clearDismissed(activeUserIdRef.current);
		}
		activeUserIdRef.current = null;
		loadedForUserRef.current = null;
		loadingRef.current = false;
		setPendingOrders([]);
		setDismissed(new Set());
	}, []);

	// ── Carga de órdenes pendientes ──────────────────────────────────────────
	const runCheck = useCallback(async (userId: number, force = false) => {
		if (loadingRef.current) return;
		if (!force && loadedForUserRef.current === userId) return;

		loadingRef.current = true;
		try {
			const pending = await fetchPendingOrders(userId);
			loadedForUserRef.current = userId;
			setPendingOrders(pending);
		} catch {
			// Falla silenciosamente
		} finally {
			loadingRef.current = false;
		}
	}, []);

	// ── Trigger 1: logout o token expirado ───────────────────────────────────
	useEffect(() => {
		const onLogout = () => resetSession();
		window.addEventListener(LOGOUT_EVENT, onLogout);
		return () => window.removeEventListener(LOGOUT_EVENT, onLogout);
	}, [resetSession]);

	useEffect(() => {
		if (!isAuthenticated && activeUserIdRef.current !== null) {
			resetSession();
		}
	}, [isAuthenticated, resetSession]);

	// ── Trigger 2: post-login ────────────────────────────────────────────────
	// Lee directamente de localStorage porque React aún no re-renderizó cuando
	// este evento llega (el closure tiene session/role stale = null).
	useEffect(() => {
		const onPostLogin = () => {
			// getSession() lee del store en memoria — ya fue actualizado por auth.ts
			// antes de disparar este evento, así que el closure no está stale.
			const stored = getSession();
			const userId = stored?.user?.id;
			const typeUser = stored?.user?.type_user;
			if (!userId || typeUser !== "Cliente") return;

			activeUserIdRef.current = userId;
			setDismissed(readDismissed(userId));
			loadedForUserRef.current = null;
			void runCheck(userId, true);
		};
		window.addEventListener(POST_LOGIN_EVENT, onPostLogin);
		return () => window.removeEventListener(POST_LOGIN_EVENT, onPostLogin);
	}, [runCheck]); // runCheck es estable (useCallback sin deps)

	// ── Trigger 3: recarga de página con sesión existente ───────────────────
	// Incluye isAuthenticated + role para dispararse cuando el segundo setSession
	// establece el role (el userId puede ser el mismo que antes del logout).
	useEffect(() => {
		if (!isAuthenticated || role !== "Cliente" || !session?.user?.id) {
			return;
		}
		const userId = session.user.id;
		// Evitar doble carga si POST_LOGIN_EVENT ya lo ejecutó
		if (loadedForUserRef.current === userId) return;
		activeUserIdRef.current = userId;
		setDismissed(readDismissed(userId));
		void runCheck(userId);
	}, [isAuthenticated, role, session?.user?.id, runCheck]);

	// ── Computed ─────────────────────────────────────────────────────────────
	const visibleOrder = pendingOrders.find((o) => !dismissed.has(o.orderId)) ?? null;

	const dismiss = useCallback(() => {
		if (!visibleOrder || !activeUserIdRef.current) return;
		const userId = activeUserIdRef.current;
		setDismissed((prev) => {
			const next = new Set([...prev, visibleOrder.orderId]);
			writeDismissed(userId, next);
			return next;
		});
	}, [visibleOrder]);

	const markReviewed = useCallback((orderId: number) => {
		setPendingOrders((prev) => prev.filter((o) => o.orderId !== orderId));
	}, []);

	return (
		<PendingReviewContext.Provider
			value={{ pendingOrder: visibleOrder, dismiss, markReviewed }}
		>
			{children}
			{visibleOrder && <ModalPendingReview order={visibleOrder} />}
		</PendingReviewContext.Provider>
	);
}
